/**
 * v3.10.0: GPS Source Selector Component
 * 
 * Permite ao operador selecionar qual fonte física de GPS usar para um equipamento.
 * Quando múltiplos dispositivos GPS reportam posição (ex: GPS interno, GPS CAN),
 * o operador pode escolher qual usar para evitar oscilação.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  MapPin, 
  Radio, 
  RefreshCw, 
  Check,
  AlertTriangle,
  Info,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GPSSourceSelector = ({ 
  equipmentId, 
  equipmentTag,
  isOpen, 
  onClose, 
  onSaved 
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gpsData, setGpsData] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [error, setError] = useState(null);

  // Fetch available GPS sources
  const fetchGPSSources = useCallback(async () => {
    if (!equipmentId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/equipments/${equipmentId}/gps-sources`);
      setGpsData(response.data);
      setSelectedSource(response.data.preferred_source_label || '_any_');
    } catch (err) {
      console.error('Error fetching GPS sources:', err);
      setError('Erro ao carregar fontes de GPS');
    } finally {
      setLoading(false);
    }
  }, [equipmentId]);

  useEffect(() => {
    if (isOpen && equipmentId) {
      fetchGPSSources();
    }
  }, [isOpen, equipmentId, fetchGPSSources]);

  const handleSave = async () => {
    setSaving(true);
    
    try {
      // Se selecionou "_any_", envia null para remover a configuração
      const sourceToSave = selectedSource === '_any_' ? null : selectedSource;
      
      await axios.put(
        `${API}/equipments/${equipmentId}/gps-source`,
        null,
        { params: { preferred_source_label: sourceToSave } }
      );
      
      toast.success(
        sourceToSave 
          ? `Fonte de GPS configurada: ${sourceToSave}`
          : 'Configuração de GPS removida - qualquer fonte será aceita'
      );
      
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error('Error saving GPS source:', err);
      toast.error('Erro ao salvar configuração de GPS');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-slate-900 border-slate-700" data-testid="gps-source-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-400" />
            Fonte de GPS
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {equipmentTag || equipmentId}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4 py-4">
            {/* Info Alert */}
            <Alert className="bg-blue-500/10 border-blue-500/30">
              <Info className="h-4 w-4 text-blue-400" />
              <AlertDescription className="text-sm text-blue-300">
                Selecione qual dispositivo GPS usar para determinar a posição deste equipamento.
                Isso evita oscilação quando múltiplos GPS reportam dados.
              </AlertDescription>
            </Alert>

            {/* Source Count */}
            <div className="flex items-center gap-2">
              <Radio className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {gpsData?.source_count || 0} fonte(s) de GPS detectada(s)
              </span>
            </div>

            {/* Source Selector */}
            <div className="space-y-2">
              <Label htmlFor="gps-source">Fonte Preferencial</Label>
              <Select
                value={selectedSource || '_any_'}
                onValueChange={setSelectedSource}
              >
                <SelectTrigger data-testid="gps-source-select">
                  <SelectValue placeholder="Selecione uma fonte..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_any_">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">(Aceitar qualquer fonte)</span>
                    </div>
                  </SelectItem>
                  
                  {gpsData?.available_sources?.map((source) => (
                    <SelectItem key={source} value={source}>
                      <div className="flex items-center gap-2">
                        <Radio className="w-3 h-3 text-green-400" />
                        <span className="font-mono text-sm">{source}</span>
                        {source === gpsData?.preferred_source_label && (
                          <Badge variant="secondary" className="ml-2 text-xs">Atual</Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {gpsData?.available_sources?.length === 0 && (
                <p className="text-xs text-yellow-500">
                  Nenhuma fonte de GPS foi detectada ainda. 
                  As fontes aparecerão automaticamente quando o equipamento enviar dados.
                </p>
              )}
            </div>

            {/* Current Selection Info */}
            {gpsData?.preferred_source_label && (
              <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-green-300">
                    Configurado: <span className="font-mono">{gpsData.preferred_source_label}</span>
                  </span>
                </div>
              </div>
            )}

            {/* Warning about rejection */}
            {selectedSource && selectedSource !== '_any_' && (
              <Alert className="bg-yellow-500/10 border-yellow-500/30">
                <AlertTriangle className="h-4 w-4 text-yellow-400" />
                <AlertDescription className="text-sm text-yellow-300">
                  Dados de GPS de outras fontes serão ignorados. 
                  Certifique-se de que a fonte selecionada está funcionando corretamente.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={fetchGPSSources} 
            disabled={loading || saving}
            className="mr-auto"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={loading || saving}
            data-testid="save-gps-source-btn"
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GPSSourceSelector;
